import {
  ActionUpdateOutcomeEnum,
  MatchActionUpdateOutcomeEnum,
  SimplifiedUserRole,
} from '@platform/client/schemas';
import { FormsMultilineInput, Modal, ModalCallToAction } from '@platform/components';
import { useState } from 'react';
import { SelectionContent } from './SelectionContent';
import {
  ErrorConfirmationContent,
  SubmittingContent,
  SuccessConfirmationContent,
} from './ConfirmationContent';
import { ProjectHub } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { OtherUpdateContent } from './OtherUpdateContent';
import {
  Control,
  FieldError,
  FormState,
  Path,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
} from 'react-hook-form';
import { useApplicationLocalStorage } from '../../application/application-hook';
import { UnresponsiveUpdateContent } from './UnresponsiveUpdateContent';

export enum ActionUpdateModalStage {
  Update_Outcome = 'update-outcome',
  Project_Complete_Deliverables = 'project-complete-deliverables',
  Project_Complete_Confirmed = 'project-complete-confirmation',
  Different_Or_In_Progress = 'different-or-in-progress',
  Project_In_Progress_Confirmed = 'project-in-progress-confirmed',
  Unresponsive = 'unresponsive',
  Unresponsive_Confirmed = 'nonsuccess-update-confirmed',
  Submitting = 'submitting',
  Error = 'error',
}

export type ActionUpdateFormData = {
  outcome: MatchActionUpdateOutcomeEnum;
  notes: string | null;
};

export const ActionUpdateModal = (props: {
  isOpen: boolean;
  onClose: () => void;
  userType: SimplifiedUserRole;
  isTeam: boolean;
}) => {
  const { matchGroupId } = useParams<{ matchGroupId: string }>();
  const [modalStage, setModalStage] = useState<ActionUpdateModalStage>(
    ActionUpdateModalStage.Update_Outcome,
  );
  const { handleSubmit, control, formState, register, reset } = useForm<ActionUpdateFormData>();
  const { updateApplicationData, getApplicationDataByKey, clearApplicationData } =
    useApplicationLocalStorage<ActionUpdateFormData>('project-hub-action');

  const modalProgress = getModalProgress(modalStage);
  const closeModal = () => {
    setModalStage(ActionUpdateModalStage.Update_Outcome);
    props.onClose();
  };

  const queryClient = useQueryClient();
  const postActionUpdateMutator = ProjectHub.usePostActionUpdate({
    mutation: {
      onMutate: () => {
        setModalStage(ActionUpdateModalStage.Submitting);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataByMatchGroupQueryKey(parseInt(matchGroupId)),
        });
        const selectedOutcome = getApplicationDataByKey('outcome');
        if (selectedOutcome === MatchActionUpdateOutcomeEnum.complete) {
          setModalStage(ActionUpdateModalStage.Project_Complete_Confirmed);
        } else if (
          selectedOutcome === MatchActionUpdateOutcomeEnum.in_progress ||
          selectedOutcome === MatchActionUpdateOutcomeEnum.different_update
        ) {
          setModalStage(ActionUpdateModalStage.Project_In_Progress_Confirmed);
        } else if (selectedOutcome === MatchActionUpdateOutcomeEnum.unresponsive) {
          setModalStage(ActionUpdateModalStage.Unresponsive_Confirmed);
        }
        clearApplicationData();
        reset();
      },
      onError: () => {
        setModalStage(ActionUpdateModalStage.Error);
      },
    },
  });
  const onSubmit: SubmitHandler<ActionUpdateFormData> = (data) => {
    postActionUpdateMutator.mutate({
      data: {
        matchGroupId: parseInt(matchGroupId),
        outcome: getApplicationDataByKey('outcome'),
        notes: data.notes ?? null,
        isTeam: props.isTeam,
      },
    });
  };
  const ctaProps = getCTAProps(
    formState,
    getApplicationDataByKey,
    onSubmit,
    handleSubmit,
    modalStage,
    setModalStage,
    closeModal,
  );

  const updateSelectionItems = [
    {
      value: MatchActionUpdateOutcomeEnum.complete,
      title: 'We completed this project',
    },
    {
      value: MatchActionUpdateOutcomeEnum.in_progress,
      title: `We are still working on it`,
    },
    {
      value: MatchActionUpdateOutcomeEnum.unresponsive,
      title: `The ${props.userType === SimplifiedUserRole.PROFESSIONAL ? 'organization' : 'professional'} is unresponsive`,
    },
    {
      value: MatchActionUpdateOutcomeEnum.different_update,
      title: 'I have a different update',
    },
  ];

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={closeModal}
      title='Action update'
      headerDivision='step'
      progress={modalProgress}
      variant='full-screen'
      primaryCTA={ctaProps.primaryCTA}
      secondaryCTA={ctaProps.secondaryCTA}
    >
      {modalStage === ActionUpdateModalStage.Update_Outcome && (
        <SelectionContent
          register={() => register('outcome')}
          description='What is the status of your project?'
          items={updateSelectionItems}
          onChange={(value) => {
            updateApplicationData({ outcome: value as ActionUpdateOutcomeEnum });
          }}
          defaultValue={getApplicationDataByKey('outcome')}
        />
      )}
      {modalStage === ActionUpdateModalStage.Project_Complete_Deliverables && (
        <DeliverablesContent control={control} formState={formState} name='notes' />
      )}
      {modalStage === ActionUpdateModalStage.Different_Or_In_Progress && (
        <OtherUpdateContent control={control} formState={formState} name='notes' />
      )}
      {modalStage === ActionUpdateModalStage.Unresponsive && (
        <UnresponsiveUpdateContent control={control} name='notes' />
      )}
      {modalStage === ActionUpdateModalStage.Project_Complete_Confirmed && (
        <SuccessConfirmationContent>
          <p>Success! You made it to the finish line.</p>
          <p>
            Your project has been marked complete, and you've unlocked the final step: Reflection.
            Thanks for your dedication — we can't wait to help you celebrate what you accomplished
            together.
          </p>
        </SuccessConfirmationContent>
      )}
      {modalStage === ActionUpdateModalStage.Project_In_Progress_Confirmed && (
        <SuccessConfirmationContent>
          Thanks for the update! We'll review what you shared and check in if there's anything we
          can do to help keep your project on track.
        </SuccessConfirmationContent>
      )}
      {modalStage === ActionUpdateModalStage.Unresponsive_Confirmed && (
        <SuccessConfirmationContent>
          Thanks for the update! We'll follow up with you by email soon.
        </SuccessConfirmationContent>
      )}
      {modalStage === ActionUpdateModalStage.Submitting && <SubmittingContent />}
      {modalStage === ActionUpdateModalStage.Error && <ErrorConfirmationContent />}
    </Modal>
  );
};

const DeliverablesContent = ({
  name,
  control,
  formState,
}: {
  name: Path<ActionUpdateFormData>;
  control: Control<ActionUpdateFormData>;
  formState: FormState<ActionUpdateFormData>;
}) => {
  return (
    <div className='flex flex-col gap-4 md:mt-8 items-center md:w-[500px]'>
      <div className='text-center text-lg'>
        Congrats on completing your project! Tell us in 2-3 sentences what you delivered/what your
        results were
      </div>
      <div className='mt-4 w-full items-stretch'>
        <FormsMultilineInput
          control={control}
          name={name}
          placeholder='Enter a description...'
          rules={{ required: `Tell us what you delivered/what your results were to continue` }}
          formsError={formState.errors[name] as FieldError}
          characterCount={800}
        />
      </div>
    </div>
  );
};

const getCTAProps = (
  formState: FormState<ActionUpdateFormData>,
  getApplicationData: (key: keyof ActionUpdateFormData) => ActionUpdateOutcomeEnum | undefined,
  onSubmit: SubmitHandler<ActionUpdateFormData>,
  handleSubmit: UseFormHandleSubmit<ActionUpdateFormData>,
  modalStage: ActionUpdateModalStage,
  setModalStage: (modalStage: ActionUpdateModalStage) => void,
  closeModal: () => void,
): { primaryCTA: ModalCallToAction; secondaryCTA?: ModalCallToAction } => {
  switch (modalStage) {
    case ActionUpdateModalStage.Update_Outcome:
      return {
        primaryCTA: {
          text: 'Save & Continue',
          callback: () => {
            const outcomeSelection = getApplicationData('outcome');
            if (outcomeSelection === ActionUpdateOutcomeEnum.complete) {
              setModalStage(ActionUpdateModalStage.Project_Complete_Deliverables);
            } else if (
              outcomeSelection === ActionUpdateOutcomeEnum.in_progress ||
              outcomeSelection === ActionUpdateOutcomeEnum.different_update
            ) {
              setModalStage(ActionUpdateModalStage.Different_Or_In_Progress);
            } else if (outcomeSelection === ActionUpdateOutcomeEnum.unresponsive) {
              setModalStage(ActionUpdateModalStage.Unresponsive);
            }
          },
        },
        secondaryCTA: {
          text: 'Back',
          callback: () => {
            closeModal();
          },
        },
      };
    case ActionUpdateModalStage.Project_Complete_Deliverables:
    case ActionUpdateModalStage.Different_Or_In_Progress:
    case ActionUpdateModalStage.Unresponsive:
      return {
        primaryCTA: {
          text:
            modalStage === ActionUpdateModalStage.Project_Complete_Deliverables ?
              'Submit'
            : 'Save & Continue',
          callback: () => {
            handleSubmit(onSubmit)();
          },
        },
        secondaryCTA: {
          text: 'Go back',
          callback: () => {
            setModalStage(ActionUpdateModalStage.Update_Outcome);
          },
        },
      };
    default:
      return {
        primaryCTA: {
          text: 'Close',
          callback: () => {
            closeModal();
          },
        },
      };
  }
};
const getModalProgress = (modalStage: ActionUpdateModalStage): number => {
  if (
    modalStage === ActionUpdateModalStage.Project_Complete_Confirmed ||
    modalStage === ActionUpdateModalStage.Project_In_Progress_Confirmed ||
    modalStage === ActionUpdateModalStage.Unresponsive_Confirmed
  ) {
    return 100;
  } else if (modalStage === ActionUpdateModalStage.Update_Outcome) {
    return 15;
  } else {
    return 66;
  }
};
